import {
  SET_LBO_DASHBOARD,
  SET_DCF_ANALYSIS,
  SET_DEBTPANEL,
  SET_DCF_VALUATION
} from "./mutations";
import { store } from "../../index";
import { DateTime } from "luxon";
import CurrencyFormatter from "currency-formatter";
import Swal from "sweetalert2";
import LBODashboard from "../../../models/dealOffer/LBODashboard";
import DCFAnalysis from "../../../models/dealOffer/DCFAnalysis";
import DebtPanel from "../../../models/dealOffer/DebtPanel";
import DCFValuation from "../../../models/dealOffer/DCFValuation";
import { generateCapitalizationRate } from "../capitalizationRate/actions";
import {
  generateStockDealSplitReport,
  generateAssetSaleDealSplitReport,
  generateGoodwillReport,
  generateReconciliationOfValuationMethodsReport,
  setReconciliationOfValuationMethodsReport
} from "../financials/actions";
import {generateForecastFreeCashFlow} from "../forecastFCF/actions";
import { generatePurchaserAcquisitionExpenses } from "../acquisitionExpenses/actions";

//--------- generate LBO Dashboard ---------//

export const generateLBODashboard = (dealType) => async (dispatch) => {
  await dispatch(generateCapitalizationRate());
  await dispatch(generateStockDealSplitReport());
  await dispatch(generateAssetSaleDealSplitReport());
  await dispatch(generateGoodwillReport());
  await dispatch(setReconciliationOfValuationMethodsReport());
  setTimeout(() => {
    generateReconciliationOfValuationMethodsReport()
  }, 1000);
  await dispatch(generatePurchaserAcquisitionExpenses({ purchaseAcquisitionExpensesType: "stock" }));
  await dispatch(generatePurchaserAcquisitionExpenses({ purchaseAcquisitionExpensesType: "asset" }));

  const { currentLBODashboard } = store.getState().dealOffer;
  const { financingCalculator } = store.getState();
  const { capitalizationRate } = store.getState().capitalizationRate;
  const { stockPurchaserAcquisitionExpenses, assetPurchaserAcquisitionExpenses } = store.getState().acquisitionExpenses;
  const {
    currentGoodwillReport,
    stockDealSplitReport,
    assetSaleDealSplitReport,
    currentReconciliationOfValuationMethodsReport
  } = store.getState().financials;

  const interestRate = financingCalculator.interestRate || 1;
  const amortizedOver = financingCalculator.monthlyPayments || 120;
  const stockPurchase = CurrencyFormatter.unformat(stockDealSplitReport?.getTotalAssetsBuyerBuys({
    businessGoodwill: currentGoodwillReport?.goodwillAmount
  }), { code: "USD" });
  const assetPurchase = CurrencyFormatter.unformat(assetSaleDealSplitReport?.getTotalAssetsBuyerBuys({
    businessGoodwill: currentGoodwillReport?.goodwillAmount
  }), { code: "USD" });
  const fairMarketValue = currentReconciliationOfValuationMethodsReport?.minorityShareholderDiscountDeterminedFinalValue;
  let transactionCosts = financingCalculator.method === "None" || financingCalculator.method === "Manual"
    ? financingCalculator.acquisitionExpensesWorkingCapital
    : dealType === "STOCK"
      ? stockPurchaserAcquisitionExpenses?.additionalCapitalNeededAtClosing
      : assetPurchaserAcquisitionExpenses?.additionalCapitalNeededAtClosing;
  const stockDebtAssumed = stockDealSplitReport.totalLiabilitiesAssumedByBuyer;
  const assetDebtAssumed = assetSaleDealSplitReport.totalLiabilitiesAssumedByBuyer;
  const riskFreeRate = capitalizationRate.afterTaxCapitalizationRate.riskFreeRate;
  const equityRiskPremium = capitalizationRate.afterTaxCapitalizationRate.equityRiskPremium;
  const sizePremium = capitalizationRate.afterTaxCapitalizationRate.sizePremium;
  const industryPremium = capitalizationRate.afterTaxCapitalizationRate.industryRisk;
  const countryRiskPremium = capitalizationRate.afterTaxCapitalizationRate.countryRisk;
  const companyRiskPremium = capitalizationRate.afterTaxCapitalizationRate.riskPremiumCompanySpecificRisks;
  const summaryPercentPaid = financingCalculator.downPaymentPercentage || 90;

  let _currentLBODashboard;

  if (!currentLBODashboard) {
    _currentLBODashboard = new LBODashboard({
      dealType,
      interestRate,
      amortizedOver,
      stockPurchase,
      assetPurchase,
      fairMarketValue,
      transactionCosts,
      stockDebtAssumed,
      assetDebtAssumed,
      riskFreeRate,
      equityRiskPremium,
      sizePremium,
      industryPremium,
      countryRiskPremium,
      companyRiskPremium,
      summaryPercentPaid
    });
  } else {
    let sharePriceType = currentLBODashboard.sharePriceType;
    if(sharePriceType === "STOCK" || sharePriceType === "ASSET") sharePriceType = dealType;
    if(currentLBODashboard.transactionCostType === "MANUAL") transactionCosts = currentLBODashboard.transactionCosts;

    _currentLBODashboard = new LBODashboard({
      ...currentLBODashboard,
      dealType,
      sharePriceType,
      interestRate,
      amortizedOver,
      stockPurchase,
      assetPurchase,
      fairMarketValue,
      transactionCosts,
      stockDebtAssumed,
      assetDebtAssumed,
      riskFreeRate,
      equityRiskPremium,
      sizePremium,
      industryPremium,
      countryRiskPremium,
      companyRiskPremium,
      summaryPercentPaid
    });
  }

  dispatch({
    type: SET_LBO_DASHBOARD,
    payload: _currentLBODashboard,
  });
};

//--------- update LBO Dashboard ---------//

export const updateLBODashboard = (name, value) => (dispatch) => {
  const { currentLBODashboard } = store.getState().dealOffer;
  const { financingCalculator } = store.getState();
  const { stockPurchaserAcquisitionExpenses, assetPurchaserAcquisitionExpenses } = store.getState().acquisitionExpenses;

  if(value === "STOCK" || value === "ASSET") {
    //set share price type first
    currentLBODashboard[name] = value;
    dispatch({
      type: SET_LBO_DASHBOARD,
      payload: new LBODashboard({ ...currentLBODashboard }),
    });
    //when sharePriceType is changing to STOCK or ASSET, we must regenerate the whole dashboard because of the transaction costs
    dispatch(generateLBODashboard(value));
    return;
  }
  
  if(name === "transactionCostType" && value === "AUTO") {
    currentLBODashboard.transactionCosts = financingCalculator.method === "None" || financingCalculator.method === "Manual"
    ? financingCalculator.acquisitionExpensesWorkingCapital
    : currentLBODashboard.dealType === "STOCK"
      ? stockPurchaserAcquisitionExpenses?.additionalCapitalNeededAtClosing
      : assetPurchaserAcquisitionExpenses?.additionalCapitalNeededAtClosing;
  }
  if(name !== "earnoutTarget" && name !== "sharePriceType" && name !== "transactionCostType")
    currentLBODashboard[name] = Number(value.replace(/,/g, ""));
  else currentLBODashboard[name] = value;

  if(name === "capacity") dispatch(generateDebtPanel(currentLBODashboard.dealType));  //debtPanel changes when LOC limit changes

  //alerts for inputs
  if(currentLBODashboard.balloonPaymentAfter === 0) {
    currentLBODashboard.balloonPaymentAfter = currentLBODashboard.amortizedOver;
    Swal.fire("Error Setting Inputs!", "The input \"Balloon Payment after\" can't be zero.", "warning");
  }   //That's why balloon payment month can't be zero. It means we repay the owner financing right away.
  else if(currentLBODashboard.balloonPaymentAfter > currentLBODashboard.amortizedOver) {
    currentLBODashboard.balloonPaymentAfter = currentLBODashboard.amortizedOver;
    Swal.fire("Error Setting Inputs!", "The input \"Balloon Payment after\" can't be bigger than \"Amortized over\".", "warning");
  }
  if(currentLBODashboard.gracePeriod >= currentLBODashboard.balloonPaymentAfter) {
    if(name === "gracePeriod") {
      currentLBODashboard.gracePeriod = 0;
      Swal.fire("Error Setting Inputs!", "The input \"Grace Period\" must be smaller than \"Balloon Payment after\".", "warning");
    }
    else {
      currentLBODashboard.balloonPaymentAfter = currentLBODashboard.gracePeriod + 1;
      Swal.fire("Error Setting Inputs!", "The input \"Balloon Payment after\" must be bigger than \"Grace Period\".", "warning");
    }
  }
  if(currentLBODashboard.earnoutYears > currentLBODashboard.exitYear) {
    currentLBODashboard.earnoutYears = currentLBODashboard.exitYear;
    Swal.fire("Error Setting Inputs!", "The input \"Number of Earnout Years\" can't be bigger than \"Exit Year\".", "warning");
  }
  if(currentLBODashboard.consultingPeriod > currentLBODashboard.exitYear * 12) {
    currentLBODashboard.consultingPeriod = currentLBODashboard.exitYear * 12;
    Swal.fire("Error Setting Inputs!", "The consulting period can't be bigger than total years.", "warning");
  }
  
  dispatch({
    type: SET_LBO_DASHBOARD,
    payload: new LBODashboard({ ...currentLBODashboard }),
  });
};

//--------- generate DCF Analysis ---------//

export const generateDCFAnalysis = () => (dispatch) => {
  //Dispatches are not actually neccessary because we generate LBODashboard when we choose DealOffer tab
  //dispatch(generateLBODashboard(dealType));

  const { dealDCFAnalysis, currentLBODashboard } = store.getState().dealOffer;
  const { forecastedDOE } = store.getState().valuation;
  const { financingCalculator } = store.getState();

  const exitYear = currentLBODashboard.exitYear;
  const baseYear = forecastedDOE.yearsTotal[forecastedDOE.yearsTotal.length - 1];
  const longTermGrowthRate = currentLBODashboard.longTermGrowthRate;
  const baseFCF = financingCalculator.cashFlowMetrics.rows?.length > 2 && financingCalculator.cashFlowMetrics.rows[2].value;
  const earnoutYears = currentLBODashboard.earnoutYears;
  const earnoutComp = currentLBODashboard.compensation;
  const consultingMonths = currentLBODashboard.consultingPeriod;
  const consultingFee = currentLBODashboard.consultingFee;
  const fixedSynergiesRate = currentLBODashboard.fixedSynergiesRate;
  let yearsFormatted = [];
  for(let i = 0; i < exitYear; i++)
    yearsFormatted.push(DateTime.fromJSDate(new Date(baseYear)).plus({ years: i + 1 }).toFormat("LLLL y"));

  let _dealDCFAnalysis;

  if (!dealDCFAnalysis) {
    _dealDCFAnalysis = new DCFAnalysis({
      exitYear,
      yearsFormatted,
      longTermGrowthRate,
      baseFCF,
      earnoutYears,
      earnoutComp,
      consultingMonths,
      consultingFee,
      fixedSynergiesRate,
    });
  } else {
    let len = dealDCFAnalysis.variableSynergiesRateSeries.length;
    for(let i = 0; i < Math.abs(len - exitYear); i++) {
      //decrease or increase the length of variableSynergiesRateSeries
      if(exitYear < len) dealDCFAnalysis.variableSynergiesRateSeries.pop();
      else dealDCFAnalysis.variableSynergiesRateSeries.push(0);
      //decrease or increase the length of every element's series of manualSynergiesRateMatrix
      for(let j = 0; j < dealDCFAnalysis.manualSynergiesRateMatrix.length; j++) {
        if(exitYear < len) dealDCFAnalysis.manualSynergiesRateMatrix[j].series.pop();
        else dealDCFAnalysis.manualSynergiesRateMatrix[j].series.push(0);
      }      
    }

    _dealDCFAnalysis = new DCFAnalysis({
      ...dealDCFAnalysis,
      exitYear,
      yearsFormatted,
      longTermGrowthRate,
      baseFCF,
      earnoutYears,
      earnoutComp,
      consultingMonths,
      consultingFee,
      fixedSynergiesRate,
    });
  }

  dispatch({
    type: SET_DCF_ANALYSIS,
    payload: _dealDCFAnalysis,
  });
};

//--------- update DCF Analysis when input values are changing ---------//

export const updateDCFAnalysis = (type, value, index, id) => async (dispatch) => {
  const { dealDCFAnalysis, currentLBODashboard } = store.getState().dealOffer;

  if(type === "Selection") {
    dealDCFAnalysis.synergiesType = value;
    dispatch(generateDebtPanel(currentLBODashboard.dealType));  //debtPanel changes when syergy type changes
  }
  else if(type === "Series") dealDCFAnalysis.variableSynergiesRateSeries[index] = Number(value.replace(/,/g, ""));
  else if(type === "MatrixValue") {
    dealDCFAnalysis.manualSynergiesRateMatrix[id].series[index] = Number(value.replace(/,/g, ""));
    dispatch(generateDebtPanel(currentLBODashboard.dealType));  //debtPanel changes when synergy matrix array value changes
  }
  else if(type === "MatrixName"){
    let matrix = dealDCFAnalysis.manualSynergiesRateMatrix;
    let len = matrix.length; //length of the whole matrix

    matrix[id].name = value;
    if (matrix[len - 1].name !== "") {
      //add a new item(object) to matrix when the name of last item exists
      matrix[len] = {
        name: "",
        series: dealDCFAnalysis.yearsFormatted.map(() => 0),
      };
    } else {
      //delete the last item(object) of matrix when the name of 2nd last item is null
      if (len > 1 && matrix[len - 2].name === "") matrix.pop();      //decrease the number of items
    }
  }

  dispatch({
    type: SET_DCF_ANALYSIS,
    payload: new DCFAnalysis({
      ...dealDCFAnalysis,
    }),
  });
};

//--------- generate DebtPanel ---------//

export const generateDebtPanel = (dealType) => async (dispatch) => {
  //Top 2 are not actually neccessary because we generate them in the LBODashboard
  //dispatch(generateStockDealSplitReport());
  //dispatch(generateAssetSaleDealSplitReport());
  await dispatch(generateLBODashboard(dealType));
  await dispatch(generateDCFAnalysis());

  const { debtPanel, currentLBODashboard, dealDCFAnalysis } = store.getState().dealOffer;
  const { stockDealSplitReport, assetSaleDealSplitReport } = store.getState().financials;
  const { stockPurchaserAcquisitionExpenses, assetPurchaserAcquisitionExpenses } = store.getState().acquisitionExpenses;

  const yearsFormatted = dealDCFAnalysis.yearsFormatted;
  const purchasePrice = dealType === "STOCK" ? currentLBODashboard.purchasePrice : currentLBODashboard.summaryPurchasePrice;
  const debtAssumed = currentLBODashboard.debtAssumed;
  const transCoAndRest = currentLBODashboard.transactionCosts + currentLBODashboard.restructuringCharges;
  let cash;
  const minimumCashBalance = currentLBODashboard.minimumCashBalance;
  const ownerFinancing = currentLBODashboard.summaryFinancing;
  const ownerFinancingPeriod = currentLBODashboard.amortizedOver - currentLBODashboard.gracePeriod;
  const ownerFinancingMoratorium = currentLBODashboard.gracePeriod;
  const ownerFinancingBalloonMonth = currentLBODashboard.balloonPaymentAfter;
  const ownerFinancingRate = currentLBODashboard.interestRate;
  const ownerFinancingSweep = currentLBODashboard.cashSweepPortion;
  let cashPayForAddition;
  const FCFforDebtService = dealDCFAnalysis.FCFAvailable;
  const dividendsPercent = currentLBODashboard.dividendsPercent;
  const capacityOfLOC = currentLBODashboard.capacity;
  const SOFRCurrent = currentLBODashboard.SOFRCurrent;
  const SOFRStep = currentLBODashboard.SOFRStep;
  const PIKStep = currentLBODashboard.PIKStep;
  let _debtPanel;

  if (dealType === "STOCK") {
    cash = stockDealSplitReport.currentAssetsSplit.filter((item) => item.name === "Cash").map(obj => obj.amount * obj.stockDealOwner)[0];
      //if toogle is right, obj.stockDealOwner is 1, else 0. 1 means cash is included in the deal
    cashPayForAddition =
      stockPurchaserAcquisitionExpenses.workingCapitalNeededAtClosing.totalCashPaymentForAllAdditionalAssetsToBePurchased;
  }
  else {
    cash = assetSaleDealSplitReport.currentAssetsSplit.filter((item) => item.name === "Cash").map(obj => obj.amount * obj.assetSaleDealOwner)[0];
      //if toogle is right, obj.stockDealOwner is 1, else 0. 1 means cash is included in the deal
    cashPayForAddition =
      assetPurchaserAcquisitionExpenses.workingCapitalNeededAtClosing.totalCashPaymentForAllAdditionalAssetsToBePurchased;
  }

  if (!debtPanel) {
    _debtPanel = new DebtPanel({
      dealType,
      yearsFormatted,
      purchasePrice,
      debtAssumed,
      transCoAndRest,
      cash,
      minimumCashBalance,
      inputArray: [
        {
          name: "Seller Rollover Equity",
          value: 0
        },
        {
          name: "Sponsor Equity",
          value: 0
        },
        {
          name: "Line of Credit",
          value: 0,
          fee: 0,
          interestType: "Fixed",
          rate: 0
        },
        {
          name: "Owner Financing",
          moratorium: 0,
          startYear: 1,
          fee: 0,
          interestType: "Fixed"
        },
        {
          name: "Assumed Debt",
          period: 0,
          moratorium: 0,
          startYear: 1,
          fee: 0,
          interestType: "Fixed",
          rate: 0,
          floor: 0,
          repayType: "Bullet",
          sweep: 0
        },
      ],
      cashPayForAddition,
      FCFforDebtService,
      dividendsPercent,
      SOFRCurrent,
      SOFRStep,
      PIKStep
    });
  }
  else {
    _debtPanel = new DebtPanel({
      ...debtPanel,
      dealType,
      yearsFormatted,
      purchasePrice,
      debtAssumed,
      transCoAndRest,
      cash,
      minimumCashBalance,
      cashPayForAddition,
      FCFforDebtService,
      dividendsPercent,
      SOFRCurrent,
      SOFRStep,
      PIKStep
    });
  }

  _debtPanel.inputArray.filter((item) => item.name === "Line of Credit")[0].capacity = capacityOfLOC;
  _debtPanel.inputArray.filter((item) => item.name === "Owner Financing")[0].value = ownerFinancing;
  _debtPanel.inputArray.filter((item) => item.name === "Owner Financing")[0].period = ownerFinancingPeriod;
  _debtPanel.inputArray.filter((item) => item.name === "Owner Financing")[0].moratorium = ownerFinancingMoratorium;
  _debtPanel.inputArray.filter((item) => item.name === "Owner Financing")[0].balloonMonth = ownerFinancingBalloonMonth;
  _debtPanel.inputArray.filter((item) => item.name === "Owner Financing")[0].rate = ownerFinancingRate;
  _debtPanel.inputArray.filter((item) => item.name === "Owner Financing")[0].sweep = ownerFinancingSweep;
  _debtPanel.inputArray.filter((item) => item.name === "Assumed Debt")[0].value = debtAssumed;
  _debtPanel.inputArray.filter((item) => item.name === "Assumed Debt")[0].repayType = "Linear";

  dispatch({
    type: SET_DEBTPANEL,
    payload: _debtPanel,
  });
};

//--------- update DebtPanel when input values are changing ---------//

export const updateDebtPanel = (item, value, index) => async (dispatch) => {
  const { debtPanel } = store.getState().dealOffer;
  if(item === "name" || item === "interestType" || item === "repayType") debtPanel.inputArray[index][item] = value;
  else debtPanel.inputArray[index][item] = Number(value.replace(/,/g, ""));

  dispatch({
    type: SET_DEBTPANEL,
    payload: new DebtPanel({
      ...debtPanel,
    }),
  });
};

//--------- update DebtPanel when one of the 4 types of buttons is pressed ---------//

export const onClickDebtPanel = (index, type) => async (dispatch) => {
  //add, remove, change orders of inputArray

  const { debtPanel } = store.getState().dealOffer;

  if(type === "Insert")
    debtPanel.inputArray.splice(index + 1, 0, {
      name: "",
      value: 0,
      period: 0,
      moratorium: 0,
      startYear: 1,
      fee: 0,
      interestType: "Fixed",
      rate: 0,
      floor: 0,
      repayType: "Bullet",
      sweep: 0,
      pikRate: 0
    });
  else if(type === "Remove") debtPanel.inputArray.splice(index, 1);
  else if(type === "Up" && index > 0) {
    debtPanel.inputArray.splice(index - 1, 0, debtPanel.inputArray[index]);
    debtPanel.inputArray.splice(index + 1, 1);
  }
  else if(type === "Down" && index < debtPanel.inputArray.length - 1) {
    debtPanel.inputArray.splice(index + 2, 0, debtPanel.inputArray[index]);
    debtPanel.inputArray.splice(index, 1);
  }

  dispatch({
    type: SET_DEBTPANEL,
    payload: new DebtPanel({
      ...debtPanel,
    }),
  });
};

//--------- generate DCF Valuation ---------//

export const generateDCFValuation = (dealType) => async (dispatch) => {
  await dispatch(generateForecastFreeCashFlow());
  await dispatch(generateDebtPanel(dealType));
  await dispatch(generatePurchaserAcquisitionExpenses({ purchaseAcquisitionExpensesType: "stock" }));
  await dispatch(generatePurchaserAcquisitionExpenses({ purchaseAcquisitionExpensesType: "asset" }));

  const { currentLBODashboard, dealDCFAnalysis, debtPanel, dealDCFValuation } = store.getState().dealOffer;
  const { stockDealSplitReport, assetSaleDealSplitReport } = store.getState().financials;
  const { forecastedFreeCashFlowAfterTax } = store.getState().forecastFCF.forecastFreeCashFlow;

  let yearsFormatted = [...dealDCFAnalysis.yearsFormatted];   //this years series include the base year, so is different from others
  yearsFormatted.splice(0, 0, DateTime.fromJSDate(new Date(yearsFormatted[0])).plus({ years: -1 }).toFormat("LLLL y"));
  const equityDiscountRate = currentLBODashboard.buildUpMethod / 100;
  const terminalGrowthRate = currentLBODashboard.riskFreeRate / 100;
  let FCFE = [...debtPanel.debtAndCashEngine.cashAvailableForEquity];
  FCFE.splice(0, 0, {date: yearsFormatted[0], value: -debtPanel.sponsorEquity, disabled: false});  //put -sponsorEquity to the baseYear of FCFE array
  let debtArray = debtPanel.debtAndCashEngine.debtSumArray;
  debtArray.splice(0, 0, { date: yearsFormatted[0], value: 0, disabled: true });
  let repaymentArray = debtPanel.debtAndCashEngine.repaymentArray;
  repaymentArray.splice(0, 0, { date: yearsFormatted[0], value: 0, disabled: true });
  const EBITDA = forecastedFreeCashFlowAfterTax.ebitda;
  const EBIT = forecastedFreeCashFlowAfterTax.ebit;
  const ownerFinancing = debtPanel.debtAndCashEngine.ownerFinancing;
  const lineOfCredit = debtPanel.debtAndCashEngine.lineOfCredit;
  const otherDebtsArray = debtPanel.debtAndCashEngine.otherDebtsArray;
  const startCash = debtPanel.cash;
  const endCash = debtPanel.debtAndCashEngine.cashBalance;
  let securities = 0;
  const startDebt = debtPanel.inputArray.filter((debt) => debt.name.indexOf("Equity") < 0).reduce((acc, debt) => acc + debt.value, 0);
  const reinvestRate = currentLBODashboard.reinvestmentRate / 100;

  if (dealType === "STOCK")
    securities = stockDealSplitReport.currentAssetsSplit.filter((item) => item.name === "Marketable Securities")
      .map(obj => obj.amount * obj.stockDealOwner)[0] || 0; //if stockDealOwner is 1, that means securities is included in the deal
  else
    securities = assetSaleDealSplitReport.currentAssetsSplit.filter((item) => item.name === "Marketable Securities")
      .map(obj => obj.amount * obj.assetSaleDealOwner)[0] || 0; //if assetSaleDealOwner is 1, that means securities is included in the deal

  let _dealDCFValuation;

  if (!dealDCFValuation) {
    _dealDCFValuation = new DCFValuation({
      dealType,
      yearsFormatted,
      equityDiscountRate,
      terminalGrowthRate,
      FCFE,
      debtArray,
      repaymentArray,
      EBITDA,
      EBIT,
      ownerFinancing,
      lineOfCredit,
      otherDebtsArray,
      startCash,
      endCash,
      securities,
      startDebt,
      reinvestRate
    });
  } else {
    _dealDCFValuation = new DCFValuation({
      ...dealDCFValuation,
      dealType,
      yearsFormatted,
      equityDiscountRate,
      terminalGrowthRate,
      FCFE,
      debtArray,
      repaymentArray,
      EBITDA,
      EBIT,
      ownerFinancing,
      lineOfCredit,
      otherDebtsArray,
      startCash,
      endCash,
      securities,
      startDebt,
      reinvestRate
    });
  }

  dispatch({
    type: SET_DCF_VALUATION,
    payload: _dealDCFValuation,
  });
};

//--------- update dealType of Deal Offer Tab when dealType selection is changing ---------//

export const updateDealType = (dealType) => async (dispatch) => {
  dispatch(generateLBODashboard(dealType));
  dispatch(generateDebtPanel(dealType));
  dispatch(generateDCFValuation(dealType));
};